/**
 * Реестр платежей на утверждение (ФН-03, ФН-15, ФН-16): финансист собирает переданные на оплату заявки
 * юрлица на день оплаты — с отбором по проекту, договору, сроку и сумме; заявка без договора, статьи ФП,
 * планового платежа в ДДС или основания в реестр не входит. Финансовый менеджер утверждает с телефона,
 * затем выгрузка в 1С и файл для клиент-банка.
 */
import {
  PAYMENT_REGISTRY_STATUSES,
  PAYMENT_REQUEST_STATUSES,
  type PaymentRegistriesResponse,
  type PaymentRegistryLine,
  type PaymentRegistryStatus,
  type PaymentRegistryView,
  seesMoney,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Download, Plus } from 'lucide-react';
import { useState } from 'react';
import { api } from '../../lib/api';
import { date, dateTime, tenge, todayLocal } from '../../lib/format';
import { projectsQuery } from '../../lib/projects';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, type Tone, toast } from '../../ui/feedback';
import { Checkbox, Field, Input, Select, Textarea } from '../../ui/form';
import { Panel } from '../../ui/layout';

const TONE: Record<PaymentRegistryStatus, Tone> = {
  draft: 'neutral',
  submitted: 'warning',
  approved: 'success',
  cancelled: 'neutral',
};
const num = (v: string) => Number(v.replace(/\s/g, '').replace(',', '.'));
const KEY = ['finance', 'payment-registries'];

export function PaymentRegistryTab({ registryId }: { registryId?: string }) {
  const me = useMe();
  const money = seesMoney(me.access, 'acts_invoices');
  if (!money)
    return (
      <EmptyState title="Реестр платежей — с суммами">
        Реестр показывают сотрудникам финансов с уровнем денег «Акты и счета» или выше.
      </EmptyState>
    );
  return registryId ? <RegistryCard id={registryId} /> : <RegistryList />;
}

/* ───────────────────────── Список ───────────────────────── */

function RegistryList() {
  const [status, setStatus] = useState<PaymentRegistryStatus | ''>('');
  const [creating, setCreating] = useState(false);
  const q = useQuery({
    queryKey: [...KEY, status],
    queryFn: () =>
      api<PaymentRegistriesResponse>('/finance/payment-registries', {
        query: { status: status || undefined },
      }),
  });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const d = q.data;
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Состояние" className="min-w-[180px]">
          <Select
            aria-label="Состояние"
            value={status}
            onChange={(e) => setStatus(e.target.value as PaymentRegistryStatus | '')}
          >
            <option value="">Все</option>
            {Object.entries(PAYMENT_REGISTRY_STATUSES).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </Select>
        </Field>
        {d.can.create ? (
          <Button
            variant="primary"
            icon={<Plus className="size-4" aria-hidden />}
            onClick={() => setCreating(true)}
          >
            Новый реестр
          </Button>
        ) : null}
      </div>
      {d.registries.length === 0 ? (
        <EmptyState title="Реестров платежей нет">
          Финансист собирает реестр на день оплаты из заявок, переданных на оплату; руководитель утверждает
          его, в том числе с телефона.
        </EmptyState>
      ) : (
        <ul className="flex flex-col gap-2">
          {d.registries.map((r) => (
            <li key={r.id}>
              <Link
                to="/finance"
                search={{ tab: 'registry', registryId: r.id }}
                className="flex min-h-11 flex-wrap items-center justify-between gap-x-4 gap-y-1 rounded-lg border border-line bg-surface px-4 py-3 hover:border-line-strong"
              >
                <span className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-ink">{r.number}</span>
                  <Badge tone={TONE[r.status]}>{PAYMENT_REGISTRY_STATUSES[r.status]}</Badge>
                  <span className="text-[13px] text-ink-2">
                    {r.legalEntityName} · оплата {date(r.payDate)}
                  </span>
                </span>
                <span className="text-[14px] text-ink">
                  {r.count} заяв. · {tenge(r.total)}
                  {r.status === 'approved' ? ` · оплачено ${r.paidCount} из ${r.count}` : ''}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
      <CreateDialog open={creating} onOpenChange={setCreating} les={d.legalEntities} />
    </div>
  );
}

function CreateDialog({
  open,
  onOpenChange,
  les,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  les: Array<{ id: string; name: string }>;
}) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [f, setF] = useState({ legalEntityId: '', payDate: todayLocal(), payerAccount: '', note: '' });
  const m = useMutation({
    mutationFn: () =>
      api<PaymentRegistryView>('/finance/payment-registries', {
        method: 'POST',
        body: {
          legalEntityId: f.legalEntityId || les[0]?.id,
          payDate: f.payDate,
          payerAccount: f.payerAccount.trim() || null,
          note: f.note.trim() || null,
        },
      }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: KEY });
      onOpenChange(false);
      toast(`Реестр ${r.number} создан`);
      void navigate({ to: '/finance', search: { tab: 'registry', registryId: r.id } });
    },
  });
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Новый реестр платежей"
      description="Реестр — на одно юрлицо и один день оплаты"
      footer={
        <Button variant="primary" loading={m.isPending} onClick={() => m.mutate()}>
          Создать
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="Юрлицо">
          <Select
            aria-label="Юрлицо"
            value={f.legalEntityId || les[0]?.id || ''}
            onChange={(e) => setF({ ...f, legalEntityId: e.target.value })}
          >
            {les.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="День оплаты">
          <Input
            aria-label="День оплаты"
            type="date"
            value={f.payDate}
            onChange={(e) => setF({ ...f, payDate: e.target.value })}
          />
        </Field>
        <Field label="Счёт юрлица (ИИК)" hint="Нужен файлу для клиент-банка; можно внести позже">
          <Input
            aria-label="Счёт юрлица (ИИК)"
            value={f.payerAccount}
            onChange={(e) => setF({ ...f, payerAccount: e.target.value })}
          />
        </Field>
        <Field label="Примечание">
          <Textarea
            aria-label="Примечание"
            rows={2}
            value={f.note}
            onChange={(e) => setF({ ...f, note: e.target.value })}
          />
        </Field>
        {m.isError ? <ErrorBox error={m.error} /> : null}
      </div>
    </Dialog>
  );
}

/* ───────────────────────── Карточка реестра ───────────────────────── */

/** Скачать файл: выгрузка помечает заявки выгруженными, поэтому — POST, а не ссылка */
async function download(id: string, format: '1c' | 'bank', again = false) {
  const res = await fetch(`/api/v1/finance/payment-registries/${id}/export`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'x-epc-csrf': '1', 'content-type': 'application/json' },
    body: JSON.stringify({ format, again }),
  });
  if (!res.ok) {
    const j = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new Error(j?.error?.message ?? `Ошибка ${res.status}`);
  }
  const cd = res.headers.get('content-disposition') ?? '';
  const name = decodeURIComponent(cd.match(/filename\*=UTF-8''([^;]+)/)?.[1] ?? `reestr-${format}`);
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function RegistryCard({ id }: { id: string }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: [...KEY, 'one', id],
    queryFn: () => api<PaymentRegistryView>(`/finance/payment-registries/${id}`),
  });
  const [adding, setAdding] = useState(false);
  const [approving, setApproving] = useState(false);
  const [reasonFor, setReasonFor] = useState<'return' | 'cancel' | null>(null);
  const [dropLine, setDropLine] = useState<string | null>(null);
  const [account, setAccount] = useState<string | null>(null);
  const set = (v: PaymentRegistryView) => {
    qc.setQueryData([...KEY, 'one', id], v);
    void qc.invalidateQueries({ queryKey: KEY, exact: false, predicate: (x) => x.queryKey[2] !== 'one' });
  };
  const act = useMutation({
    mutationFn: (b: {
      action: string;
      version: number;
      reason?: string;
      exclude?: Array<{ lineId: string; reason: string }>;
    }) => api<PaymentRegistryView>(`/finance/payment-registries/${id}/action`, { method: 'POST', body: b }),
    onSuccess: (v) => {
      set(v);
      setReasonFor(null);
      setDropLine(null);
      toast(`Реестр ${v.number}: ${PAYMENT_REGISTRY_STATUSES[v.status].toLowerCase()}`);
    },
  });
  const remove = useMutation({
    mutationFn: (x: { lineId: string; version: number }) =>
      api<PaymentRegistryView>(`/finance/payment-registries/${id}/lines/${x.lineId}`, {
        method: 'DELETE',
        query: { version: x.version },
      }),
    onSuccess: set,
  });
  const patch = useMutation({
    mutationFn: (b: { version: number; payerAccount: string | null }) =>
      api<PaymentRegistryView>(`/finance/payment-registries/${id}`, { method: 'PATCH', body: b }),
    onSuccess: (v) => {
      set(v);
      setAccount(null);
    },
  });
  const exp = useMutation({
    mutationFn: (x: { format: '1c' | 'bank'; again?: boolean }) => download(id, x.format, x.again),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [...KEY, 'one', id] }),
  });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const r = q.data;
  const inLines = r.lines.filter((l) => l.state === 'in');
  const outLines = r.lines.filter((l) => l.state === 'out');
  const hasIssues = inLines.some((l) => l.issues.length);
  const canAccount = r.can.edit || (r.status === 'approved' && r.can.export);
  return (
    <div className="flex flex-col gap-4">
      <Link
        to="/finance"
        search={{ tab: 'registry' }}
        className="inline-flex min-h-11 items-center gap-1.5 self-start text-[14px] text-ink-2 hover:text-ink"
      >
        <ArrowLeft className="size-4" aria-hidden /> Все реестры платежей
      </Link>
      <Panel
        title={`Реестр платежей ${r.number}`}
        meta={<Badge tone={TONE[r.status]}>{PAYMENT_REGISTRY_STATUSES[r.status]}</Badge>}
      >
        <dl className="grid gap-x-6 gap-y-2 text-[14px] sm:grid-cols-2">
          <Row k="Юрлицо" v={r.legalEntityName} />
          <Row k="День оплаты" v={date(r.payDate)} />
          <Row k="Заявок" v={`${r.count} на ${tenge(r.total)}`} />
          <Row k="Оплачено" v={`${r.paidCount} из ${r.count}`} />
          <Row k="Собрал" v={`${r.createdByName ?? '—'}, ${dateTime(r.createdAt)}`} />
          {r.submittedAt ? (
            <Row k="Передал на утверждение" v={`${r.submittedByName ?? '—'}, ${dateTime(r.submittedAt)}`} />
          ) : null}
          {r.approvedAt ? (
            <Row k="Утвердил" v={`${r.approvedByName ?? '—'}, ${dateTime(r.approvedAt)}`} />
          ) : null}
          <Row
            k="Счёт юрлица (ИИК)"
            v={
              account !== null ? (
                <span className="flex flex-wrap gap-2">
                  <Input
                    value={account}
                    onChange={(e) => setAccount(e.target.value)}
                    className="max-w-[260px]"
                  />
                  <Button
                    size="sm"
                    loading={patch.isPending}
                    onClick={() => patch.mutate({ version: r.version, payerAccount: account.trim() || null })}
                  >
                    Сохранить
                  </Button>
                </span>
              ) : (
                <span className="flex flex-wrap items-center gap-2">
                  {r.payerAccount ?? 'не указан'}
                  {canAccount ? (
                    <Button size="sm" variant="ghost" onClick={() => setAccount(r.payerAccount ?? '')}>
                      Изменить
                    </Button>
                  ) : null}
                </span>
              )
            }
          />
          {r.note ? <Row k="Примечание" v={r.note} /> : null}
          {r.returnReason && r.status === 'draft' ? <Row k="Возвращён" v={r.returnReason} /> : null}
          {r.cancelReason ? <Row k="Причина отмены" v={r.cancelReason} /> : null}
          {r.exported1cAt ? <Row k="Выгружен в 1С" v={dateTime(r.exported1cAt)} /> : null}
          {r.exportedBankAt ? <Row k="Файл клиент-банка" v={dateTime(r.exportedBankAt)} /> : null}
        </dl>
        {patch.isError ? <ErrorBox error={patch.error} className="mt-3" /> : null}
        <div className="mt-4 flex flex-wrap gap-2">
          {r.can.edit ? (
            <>
              <Button icon={<Plus className="size-4" aria-hidden />} onClick={() => setAdding(true)}>
                Добавить заявки
              </Button>
              <Button
                variant="primary"
                disabled={!inLines.length || hasIssues}
                loading={act.isPending}
                onClick={() => act.mutate({ action: 'submit', version: r.version })}
              >
                Передать на утверждение
              </Button>
            </>
          ) : null}
          {r.can.approve ? (
            <Button variant="primary" onClick={() => setApproving(true)}>
              Утвердить
            </Button>
          ) : null}
          {r.status === 'submitted' && (r.can.approve || r.can.edit) ? (
            <Button onClick={() => setReasonFor('return')}>Вернуть на доработку</Button>
          ) : null}
          {(r.status === 'draft' || r.status === 'submitted') && (r.can.edit || r.can.approve) ? (
            <Button variant="danger" onClick={() => setReasonFor('cancel')}>
              Отменить реестр
            </Button>
          ) : null}
          {r.can.export ? (
            <>
              <Button
                icon={<Download className="size-4" aria-hidden />}
                loading={exp.isPending && exp.variables?.format === '1c'}
                onClick={() => exp.mutate({ format: '1c' })}
              >
                Выгрузить в 1С
              </Button>
              <Button
                icon={<Download className="size-4" aria-hidden />}
                loading={exp.isPending && exp.variables?.format === 'bank'}
                onClick={() => {
                  // Повторный файл оплатит заявки ещё раз — только с явным подтверждением
                  if (
                    r.exportedBankAt &&
                    !window.confirm(
                      `Файл для клиент-банка уже выгружали ${dateTime(r.exportedBankAt)}. Повторный файл оплатит заявки ещё раз. Выгрузить снова?`,
                    )
                  )
                    return;
                  exp.mutate({ format: 'bank', again: Boolean(r.exportedBankAt) });
                }}
              >
                {r.exportedBankAt ? 'Файл для клиент-банка ещё раз' : 'Файл для клиент-банка'}
              </Button>
            </>
          ) : null}
        </div>
        {act.isError ? <ErrorBox error={act.error} className="mt-3" /> : null}
        {exp.isError ? <ErrorBox error={exp.error} className="mt-3" /> : null}
        {r.status === 'submitted' && !r.can.approve ? (
          <p className="mt-3 text-[13px] text-ink-2">
            Утверждает финансовый менеджер юрлица — не тот, кто собирал и передавал реестр.
          </p>
        ) : null}
      </Panel>

      <Panel
        title="Заявки в реестре"
        meta={hasIssues ? 'Есть заявки, которые не проходят условия оплаты' : undefined}
      >
        {inLines.length === 0 ? (
          <p className="text-[14px] text-ink-2">Заявок пока нет.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {inLines.map((l) => (
              <LineItem
                key={l.lineId}
                l={l}
                action={
                  r.can.drop ? (
                    <Button size="sm" variant="ghost" onClick={() => setDropLine(l.lineId)}>
                      Снять
                    </Button>
                  ) : r.can.edit ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      loading={remove.isPending && remove.variables?.lineId === l.lineId}
                      onClick={() => remove.mutate({ lineId: l.lineId!, version: r.version })}
                    >
                      Убрать
                    </Button>
                  ) : null
                }
              />
            ))}
          </ul>
        )}
        {remove.isError ? <ErrorBox error={remove.error} className="mt-3" /> : null}
      </Panel>
      {outLines.length ? (
        <Panel title="Исключённые заявки">
          <ul className="flex flex-col gap-2">
            {outLines.map((l) => (
              <LineItem key={l.lineId} l={l} />
            ))}
          </ul>
        </Panel>
      ) : null}

      {adding ? <AddDialog r={r} onClose={() => setAdding(false)} onDone={set} /> : null}
      {approving ? <ApproveDialog r={r} onClose={() => setApproving(false)} onDone={set} /> : null}
      <ReasonDialog
        open={reasonFor !== null}
        title={reasonFor === 'cancel' ? 'Отменить реестр' : 'Вернуть реестр на доработку'}
        onOpenChange={(v) => !v && setReasonFor(null)}
        loading={act.isPending}
        error={act.error}
        onSubmit={(reason) => act.mutate({ action: reasonFor!, version: r.version, reason })}
      />
      <ReasonDialog
        open={dropLine !== null}
        title="Снять заявку из утверждённого реестра"
        onOpenChange={(v) => !v && setDropLine(null)}
        loading={act.isPending}
        error={act.error}
        onSubmit={(reason) =>
          act.mutate({ action: 'drop', version: r.version, exclude: [{ lineId: dropLine!, reason }] })
        }
      />
    </div>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex flex-col">
      <dt className="text-[12px] text-ink-2">{k}</dt>
      <dd className="[overflow-wrap:anywhere] text-ink">{v}</dd>
    </div>
  );
}

function LineItem({
  l,
  action,
  pick,
}: {
  l: PaymentRegistryLine;
  action?: React.ReactNode;
  pick?: React.ReactNode;
}) {
  return (
    <li className="flex flex-col gap-1 rounded-md border border-line px-3 py-2.5">
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          {pick}
          {l.purchaseRequestId ? (
            <Link
              to="/procurement/$requestId"
              params={{ requestId: l.purchaseRequestId }}
              className="inline-flex min-h-11 items-center font-semibold text-ink underline-offset-2 hover:underline md:min-h-0"
            >
              {l.number}
            </Link>
          ) : (
            <Link
              to="/procurement"
              search={{ tab: 'payments' }}
              className="inline-flex min-h-11 items-center font-semibold text-ink underline-offset-2 hover:underline md:min-h-0"
            >
              {l.number}
            </Link>
          )}
          <span className="text-[13px] text-ink-2">{l.projectCode}</span>
          {l.requestStatus !== 'sent' ? <Badge>{PAYMENT_REQUEST_STATUSES[l.requestStatus]}</Badge> : null}
        </div>
        <div className="flex items-center gap-2">
          <span className="font-semibold whitespace-nowrap text-ink">{tenge(l.amount)}</span>
          {action}
        </div>
      </div>
      <div className="text-[13px] [overflow-wrap:anywhere] text-ink-2">
        {l.counterpartyName ?? 'Контрагент не указан'}
        {l.contractNumber ? (
          <>
            {' · договор '}
            <Link
              to="/contracts/$contractId"
              params={{ contractId: l.contractId! }}
              className="inline-flex min-h-11 items-center underline underline-offset-2 md:min-h-0"
            >
              {l.contractNumber}
            </Link>
          </>
        ) : null}
        {l.costArticle ? ` · ${l.costArticle}` : ''}
        {l.plannedDate ? ` · срок ${date(l.plannedDate)}` : ''}
      </div>
      {l.purpose ? <div className="text-[13px] [overflow-wrap:anywhere] text-ink-2">{l.purpose}</div> : null}
      {l.outReason ? <div className="text-[13px] text-ink-2">{l.outReason}</div> : null}
      {l.issues.length ? (
        <ul className="text-[13px] text-danger">
          {l.issues.map((i) => (
            <li key={i}>• {i}</li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function AddDialog({
  r,
  onClose,
  onDone,
}: {
  r: PaymentRegistryView;
  onClose: () => void;
  onDone: (v: PaymentRegistryView) => void;
}) {
  const projects = useQuery(projectsQuery('all'));
  const [f, setF] = useState({ projectId: '', dueTo: r.payDate, minAmount: '', maxAmount: '' });
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const q = useQuery({
    queryKey: [...KEY, 'candidates', r.id, f],
    queryFn: () =>
      api<PaymentRegistryLine[]>(`/finance/payment-registries/${r.id}/candidates`, {
        query: {
          projectId: f.projectId || undefined,
          dueTo: f.dueTo || undefined,
          minAmount: f.minAmount ? num(f.minAmount) : undefined,
          maxAmount: f.maxAmount ? num(f.maxAmount) : undefined,
        },
      }),
  });
  const m = useMutation({
    mutationFn: () =>
      api<PaymentRegistryView>(`/finance/payment-registries/${r.id}/lines`, {
        method: 'POST',
        body: { version: r.version, paymentRequestIds: [...picked] },
      }),
    onSuccess: (v) => {
      onDone(v);
      onClose();
      toast(`Добавлено заявок: ${picked.size}`);
    },
  });
  const ok = (q.data ?? []).filter((x) => !x.issues.length);
  const sum = ok.filter((x) => picked.has(x.paymentRequestId)).reduce((s, x) => s + x.amount, 0);
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      width="lg"
      title="Добавить заявки в реестр"
      description="Переданные на оплату заявки юрлица. Без договора, статьи ФП, планового платежа в ДДС или основания заявка в реестр не входит."
      footer={
        <Button variant="primary" disabled={!picked.size} loading={m.isPending} onClick={() => m.mutate()}>
          Добавить {picked.size ? `${picked.size} на ${tenge(sum)}` : ''}
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Проект">
            <Select
              aria-label="Проект"
              value={f.projectId}
              onChange={(e) => setF({ ...f, projectId: e.target.value })}
            >
              <option value="">Все</option>
              {(projects.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code} — {p.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Срок оплаты по">
            <Input
              aria-label="Срок оплаты по"
              type="date"
              value={f.dueTo}
              onChange={(e) => setF({ ...f, dueTo: e.target.value })}
            />
          </Field>
          <Field label="Сумма от">
            <Input
              aria-label="Сумма от"
              inputMode="decimal"
              value={f.minAmount}
              onChange={(e) => setF({ ...f, minAmount: e.target.value })}
            />
          </Field>
          <Field label="Сумма до">
            <Input
              aria-label="Сумма до"
              inputMode="decimal"
              value={f.maxAmount}
              onChange={(e) => setF({ ...f, maxAmount: e.target.value })}
            />
          </Field>
        </div>
        {q.isPending ? <Spinner /> : q.isError ? <ErrorBox error={q.error} /> : null}
        {q.data && !q.data.length ? <p className="text-[14px] text-ink-2">Подходящих заявок нет.</p> : null}
        <ul className="flex flex-col gap-2">
          {(q.data ?? []).map((l) => (
            <LineItem
              key={l.paymentRequestId}
              l={l}
              pick={
                <Checkbox
                  label=""
                  className="min-h-11 min-w-11 items-center justify-center"
                  aria-label={`Выбрать ${l.number}`}
                  disabled={l.issues.length > 0}
                  checked={picked.has(l.paymentRequestId)}
                  onChange={(e) => {
                    const n = new Set(picked);
                    if (e.target.checked) n.add(l.paymentRequestId);
                    else n.delete(l.paymentRequestId);
                    setPicked(n);
                  }}
                />
              }
            />
          ))}
        </ul>
        {m.isError ? <ErrorBox error={m.error} /> : null}
      </div>
    </Dialog>
  );
}

function ApproveDialog({
  r,
  onClose,
  onDone,
}: {
  r: PaymentRegistryView;
  onClose: () => void;
  onDone: (v: PaymentRegistryView) => void;
}) {
  const lines = r.lines.filter((l) => l.state === 'in');
  const [ex, setEx] = useState<Record<string, string>>({});
  const m = useMutation({
    mutationFn: () =>
      api<PaymentRegistryView>(`/finance/payment-registries/${r.id}/action`, {
        method: 'POST',
        body: {
          action: 'approve',
          version: r.version,
          exclude: Object.entries(ex).map(([lineId, reason]) => ({ lineId, reason })),
        },
      }),
    onSuccess: (v) => {
      onDone(v);
      onClose();
      toast(`Реестр ${v.number} утверждён`);
    },
  });
  const left = lines.filter((l) => ex[l.lineId!] === undefined);
  const bad = Object.values(ex).some((v) => v.trim().length < 3);
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={`Утвердить реестр ${r.number}`}
      description={`К оплате ${date(r.payDate)}: ${left.length} заяв. на ${tenge(left.reduce((s, l) => s + l.amount, 0))}`}
      footer={
        <Button
          variant="primary"
          disabled={!left.length || bad}
          loading={m.isPending}
          onClick={() => m.mutate()}
        >
          Утвердить
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-[13px] text-ink-2">
          Снимите отметку с заявки, которую не оплачиваем, и укажите причину.
        </p>
        <ul className="flex flex-col gap-2">
          {lines.map((l) => (
            <li key={l.lineId} className="flex flex-col gap-2 rounded-md border border-line px-3 py-2.5">
              <Checkbox
                checked={ex[l.lineId!] === undefined}
                onChange={(e) => {
                  const n = { ...ex };
                  if (e.target.checked) delete n[l.lineId!];
                  else n[l.lineId!] = '';
                  setEx(n);
                }}
                label={`${l.number} · ${tenge(l.amount)}`}
                description={`${l.counterpartyName ?? ''}${l.contractNumber ? ` · договор ${l.contractNumber}` : ''} · ${l.projectCode}`}
              />
              {ex[l.lineId!] !== undefined ? (
                <Field label="Почему не оплачиваем">
                  <Input
                    aria-label={`Почему не оплачиваем ${l.number}`}
                    value={ex[l.lineId!]}
                    onChange={(e) => setEx({ ...ex, [l.lineId!]: e.target.value })}
                  />
                </Field>
              ) : null}
            </li>
          ))}
        </ul>
        {m.isError ? <ErrorBox error={m.error} /> : null}
      </div>
    </Dialog>
  );
}

function ReasonDialog({
  open,
  title,
  onOpenChange,
  onSubmit,
  loading,
  error,
}: {
  open: boolean;
  title: string;
  onOpenChange: (v: boolean) => void;
  onSubmit: (reason: string) => void;
  loading: boolean;
  error: unknown;
}) {
  const [reason, setReason] = useState('');
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      footer={
        <Button
          variant="primary"
          disabled={reason.trim().length < 3}
          loading={loading}
          onClick={() => onSubmit(reason.trim())}
        >
          Готово
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="Причина">
          <Textarea
            aria-label="Причина"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
        {error ? <ErrorBox error={error} /> : null}
      </div>
    </Dialog>
  );
}
